import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Badge } from "components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { Plus, Minus, CalendarBlank, Building } from "@phosphor-icons/react";
import { PeriodTicketMovement } from "../types/statistics.types";

interface PeriodMovementsModalProps {
  isOpen: boolean;
  onClose: () => void;
  periodData: PeriodTicketMovement | null;
  onTicketClick?: (ticketId: number) => void;
}

export function PeriodMovementsModal({
  isOpen,
  onClose,
  periodData,
  onTicketClick,
}: PeriodMovementsModalProps) {
  if (!periodData) return null;

  const handleTicketClick = (ticketId: number) => {
    if (onTicketClick) {
      onTicketClick(ticketId);
    } else {
      window.open(`/helpdesk/${ticketId}`, "_blank");
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const totalCreated = periodData.created.length;
  const totalCompleted = periodData.completed.length;
  const netChange = totalCreated - totalCompleted;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarBlank className="h-5 w-5" weight="duotone" />
            Ticket bewegingen - {periodData.periodLabel}
          </DialogTitle>
          <div className="flex items-center gap-4 pt-2">
            <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
              <Plus className="mr-1 h-3 w-3" />
              {totalCreated} aangemaakt
            </Badge>
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
              <Minus className="mr-1 h-3 w-3" />
              {totalCompleted} afgerond
            </Badge>
            <Badge
              variant="outline"
              className={
                netChange > 0
                  ? "border-red-300 text-red-700"
                  : netChange < 0
                    ? "border-green-300 text-green-700"
                    : ""
              }
            >
              Netto: {netChange > 0 ? "+" : ""}
              {netChange}
            </Badge>
          </div>
        </DialogHeader>

        <div className="max-h-[calc(85vh-140px)] space-y-6 overflow-y-auto pr-2">
          {/* Created Tickets Section */}
          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-700">
              <Plus className="h-4 w-4" weight="bold" />
              Nieuw aangemaakt ({totalCreated})
            </h3>
            {totalCreated === 0 ? (
              <div className="text-muted-foreground rounded-md border border-dashed py-6 text-center text-sm">
                Geen tickets aangemaakt in deze periode
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-red-50/50">
                      <TableHead className="w-20">ID</TableHead>
                      <TableHead>Titel</TableHead>
                      <TableHead>Gebouw</TableHead>
                      <TableHead>Label</TableHead>
                      <TableHead className="w-28 text-right">Datum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {periodData.created.map((ticket) => (
                      <TableRow
                        key={ticket.id}
                        className="hover:bg-red-50/30 cursor-pointer"
                        onClick={() => handleTicketClick(ticket.id)}
                      >
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="border-red-200 bg-red-50 font-mono text-red-700"
                          >
                            <Plus className="mr-0.5 h-3 w-3" />H{ticket.id}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate font-medium">
                          {ticket.title}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Building className="text-muted-foreground h-3.5 w-3.5" />
                            <span className="max-w-[150px] truncate text-sm">
                              {ticket.buildingName}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {ticket.label || "Geen label"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right text-sm">
                          {formatDate(ticket.date)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Completed Tickets Section */}
          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-green-700">
              <Minus className="h-4 w-4" weight="bold" />
              Afgerond ({totalCompleted})
            </h3>
            {totalCompleted === 0 ? (
              <div className="text-muted-foreground rounded-md border border-dashed py-6 text-center text-sm">
                Geen tickets afgerond in deze periode
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-green-50/50">
                      <TableHead className="w-20">ID</TableHead>
                      <TableHead>Titel</TableHead>
                      <TableHead>Gebouw</TableHead>
                      <TableHead>Label</TableHead>
                      <TableHead className="w-28 text-right">Datum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {periodData.completed.map((ticket) => (
                      <TableRow
                        key={ticket.id}
                        className="hover:bg-green-50/30 cursor-pointer"
                        onClick={() => handleTicketClick(ticket.id)}
                      >
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="border-green-200 bg-green-50 font-mono text-green-700"
                          >
                            <Minus className="mr-0.5 h-3 w-3" />H{ticket.id}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate font-medium">
                          {ticket.title}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Building className="text-muted-foreground h-3.5 w-3.5" />
                            <span className="max-w-[150px] truncate text-sm">
                              {ticket.buildingName}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {ticket.label || "Geen label"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right text-sm">
                          {formatDate(ticket.date)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
